import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import moment from "moment";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import _ from 'underscore';
import StyledTextField from "../styledComponents/TextField";
import { UserContext } from '../utils/UserContext';
import { useAxios } from '../utils/useAxios';
import { useTranslation } from 'react-i18next';
import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete';

const UserSettings = forwardRef((props, ref) => {

    const theme = useTheme();
    const abovemd = useMediaQuery(theme.breakpoints.up("md"));
    const belowsm = useMediaQuery(theme.breakpoints.down("sm"));

    const { user, setUser } = useContext(UserContext);

    const [open, setOpen] = useState(false);
    const [update, setUpdate] = useState({});
    const [loading, setLoading] = useState(false);
    const [language, setLanguage] = useState("de-DE");
    const [representations, setRepresentations] = useState([]);
    const [virtualRepresentations, setVirtualRepresentations] = useState([]);

    const axiosInstance = useAxios();

    const titleRef = useRef("MbM");

    const { t, i18n } = useTranslation();
    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    useEffect(() => {
        if (localStorage.getItem("i18nextLng") !== undefined) {
            setLanguage(localStorage.getItem("i18nextLng"));
        }
        if (user.userID !== undefined && user.userID !== "account-not-active" && representations?.length === 0) {
            fetchRepresentations();
        }
        if (user.userID !== undefined && user.userID !== "account-not-active" && virtualRepresentations?.length === 0) {
            fetchVirtualRepresentations();
        }
        //eslint-disable-next-line
    }, [user, update.userID])

    useImperativeHandle(ref, () => ({
        open() {
            setOpen(true);
            titleRef.current = document.title;
            document.title = t("vmm-settings");
        }
    }));

    const fetchRepresentations = async () => {
        await axiosInstance.current.get("/representations", { "params": { "virtual": false } }).then(res => {
            setRepresentations([...res.data]);
            if (user.defaultProxy?.userID !== undefined) {
                setUpdate({
                    ...update,
                    defaultProxy: res.data.filter(r => r.userID === user.defaultProxy?.userID)[0]
                });
            }
        })
    };

    const fetchVirtualRepresentations = async () => {
        await axiosInstance.current.get("/representations", { "params": { "virtual": true } }).then(res => {
            setVirtualRepresentations([...res.data]);
            if (user.defaultVirtualProxy?.userID !== undefined) {
                setUpdate({
                    ...update,
                    defaultVirtualProxy: res.data.filter(r => r.userID === user.defaultVirtualProxy?.userID)[0]
                });
            }
        })
    };

    const save = async () => {
        setLoading(true);
        await axiosInstance.current({
            url: `/${user.userID}`,
            method: "PUT",
            data: update
        }).then(res => {
            if (res.status === 200) {
                setUser({
                    ...user,
                    ...res.data
                });
            }
            setLoading(false);
        }).catch(err => {
            console.error(err.response);
            setLoading(false);
        })
    };

    return (
        <Dialog
            fullScreen={!abovemd}
            fullWidth
            maxWidth={"lg"}
            open={open}
            onClose={() => { if (update.mobilboxPassword !== "") { setOpen(false); document.title = titleRef.current; } }}
            sx={{
                "& .MuiDialog-paper": {
                    borderRadius: !abovemd ? 0 : 6,
                    display: "flex",
                    alignItems: "center",
                    height: abovemd ? 525 : "100%",
                    backgroundImage: "none",
                    "& .MuiPaper-elevation": {
                        elevation: 2
                    }
                }
            }}
        >
            <DialogTitle sx={{ borderBottom: 2, borderBottomColor: "divider", width: "95%", p: 0, mt: 1 }}>
                <Grid container>
                    <Grid item>
                        <Grid container>
                            <AccountCircleIcon sx={{ color: "text.primary", fontSize: 32, pb: .5 }} />
                            <Typography color="text.primary" fontSize={20}>{t("user-settings")}</Typography>
                        </Grid>
                    </Grid>
                    <Box key="spacer" sx={{ flexGrow: 1 }} />
                    <Grid item>
                        <Grid container direction="column">
                            <Typography color="text.secondary" fontSize={10}>{t("last-edited")}</Typography>
                            <Typography color="text.secondary" fontSize={10} sx={{ display: "flex", justifyContent: "flex-end" }}>{moment(user.lastModifiedDate).format("DD/MM/YYYY")}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent sx={{ width: "95%", p: 0, "::-webkit-scrollbar": { display: "none" } }}>
                <Grid container direction="column">
                    <Grid item sx={{ my: 3 }}>
                        <Typography color="text.primary" fontSize={14}>{(t("personal-info"))}</Typography>
                        <Divider sx={{ bgcolor: "text.primary", mb: 3 }} />
                        <Grid container justifyContent="center">
                            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <Tooltip title={t("contact-an-admin")} followCursor>
                                    <StyledTextField
                                        variant="standard"
                                        label="Email"
                                        helperText=" "
                                        value={user.email !== undefined ? user.email : " "}
                                        sx={{ width: belowsm ? "70%" : "90%" }}
                                        disabled
                                    />
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <Tooltip title={t("contact-an-admin")} followCursor>
                                    <StyledTextField
                                        variant="standard"
                                        label={t("first-name")}
                                        helperText=" "
                                        value={user.surName !== undefined ? user.surName : " "}
                                        sx={{ width: belowsm ? "70%" : "90%" }}
                                        disabled
                                    />
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <Tooltip title={t("contact-an-admin")} followCursor>
                                    <StyledTextField
                                        variant="standard"
                                        label={t("surname")}
                                        helperText=" "
                                        value={user.familyName !== undefined ? user.familyName : " "}
                                        sx={{ width: belowsm ? "70%" : "90%" }}
                                        disabled
                                    />
                                </Tooltip>
                            </Grid>
                            <Grid item container xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <Tooltip title={t("contact-an-admin")} followCursor>
                                    <StyledTextField
                                        variant="standard"
                                        label={t("phone")}
                                        helperText=" "
                                        value={update.phoneNumber || ""}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            width: belowsm ? "70%" : "90%",
                                            "& .MuiFormHelperText-root": {
                                                visibility: "hidden"
                                            },
                                            "&:hover": {
                                                "& .MuiFormHelperText-root": {
                                                    visibility: "visible"
                                                }
                                            }
                                        }}
                                        disabled
                                    />
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <Tooltip title={t("contact-an-admin")} followCursor>
                                    <StyledTextField
                                        variant="standard"
                                        label={t("vpn-number")}
                                        helperText=" "
                                        value={update.vpnNumber || ""}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                                width: belowsm ? "70%" : "90%",
                                                "& .MuiFormHelperText-root": {
                                                    visibility: "hidden"
                                                },
                                                "&:hover": {
                                                    "& .MuiFormHelperText-root": {
                                                        visibility: "visible"
                                                    }
                                                }
                                            }}
                                        disabled
                                    />
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <StyledTextField
                                    variant="standard"
                                    required
                                    // type={mobilboxPassword ? "password" : "text"}
                                    label={t("voicemail-password")}
                                    helperText=" "
                                    error={!update.mobilboxPassword}
                                    value={update.mobilboxPassword || ""}
                                    onChange={(event) => {
                                        setUpdate({ ...update, mobilboxPassword: event.target.value })
                                    }}
                                    // InputProps={{
                                    //     endAdornment: (
                                    //         <InputAdornment position="end">
                                    //             <IconButton onClick={() => { setMobilboxPassword(!mobilboxPassword) }} onMouseDown={() => { setMobilboxPassword(!mobilboxPassword) }} edge="end" sx={{ mr: .1 }}>
                                    //                 {!mobilboxPassword ? <VisibilityOffIcon sx={{ color: "text.primary" }} /> : <VisibilityIcon sx={{ color: "text.primary" }} />}
                                    //             </IconButton>
                                    //         </InputAdornment>)
                                    // }}
                                    sx={{ width: belowsm ? "70%" : "90%" }}
                                    disabled={user.userID === "account-not-active"}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Typography color="text.primary" fontSize={14}>{(t("preferences"))}</Typography>
                    <Divider sx={{ bgcolor: "text.primary", mb: 3 }} />
                    <Grid container>
                        {/* <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                            <StyledTextField
                                variant="standard"
                                label={t("notification-type")}
                                helperText=" "
                                value={update.notificationType || 0}
                                onChange={(event) => {
                                    setUpdate({ ...update, notificationType: event.target.value })
                                }}
                                select
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                    MenuProps: {
                                        sx: {
                                            "& .MuiMenu-paper": {
                                                bgcolor: "background.default",
                                                backgroundImage: "none",
                                                mt: .5,
                                                width: "85%",
                                                maxWidth: 355
                                            }
                                        }
                                    }
                                }}
                                sx={{ width: belowsm ? "70%" : "90%" }}
                                disabled={user.userID === "account-not-active"}
                            >
                                <MenuItem key={"0"} value={0} style={{ whiteSpace: "normal" }}>
                                    {t("no-notification")}
                                </MenuItem>
                                <MenuItem key={"2"} value={2} style={{ whiteSpace: "normal" }}>
                                    {t("message-to-me")}
                                </MenuItem>
                                <MenuItem key={"4"} value={4} style={{ whiteSpace: "normal" }}>
                                    {t("message-to-me-and-rep")}
                                </MenuItem>
                            </StyledTextField>
                        </Grid> */}
                        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                            <StyledTextField
                                variant="standard"
                                label={t("language")}
                                helperText=" "
                                value={language || ""}
                                onChange={(event) => {
                                    setLanguage(event.target.value)
                                    changeLanguage(event.target.value);
                                }}
                                select
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                    MenuProps: {
                                        sx: {
                                            "& .MuiMenu-paper": {
                                                bgcolor: "background.default",
                                                backgroundImage: "none",
                                                mt: .5,
                                                width: "85%",
                                                maxWidth: 355
                                            }
                                        }
                                    }
                                }}
                                sx={{ width: belowsm ? "70%" : "90%" }}
                                disabled={user.userID === "account-not-active"}
                            >
                                <MenuItem key={`lang-en`} value={"en-US"} style={{ whiteSpace: "normal" }}>
                                    {t("english")}
                                </MenuItem>
                                <MenuItem key={`lang-de`} value={"de-DE"} style={{ whiteSpace: "normal" }}>
                                    {t("german")}
                                </MenuItem>
                            </StyledTextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                            {representations.length < 20 ? (
                                <StyledTextField
                                    variant="standard"
                                    label={t("default-proxy")}
                                    helperText=" "
                                    value={update.defaultProxy}
                                    onChange={(event) => {
                                        setUpdate({
                                            ...update,
                                            defaultProxy: event.target.value
                                        });
                                    }}
                                    select
                                    InputLabelProps={{ shrink: true }}
                                    SelectProps={{
                                        renderValue: (rep) => (<Typography>{`${rep.familyName} ${rep.surName}`}</Typography>),
                                        MenuProps: {
                                            sx: {
                                                "& .MuiMenu-paper": {
                                                    bgcolor: "background.default",
                                                    backgroundImage: "none",
                                                    mt: .5,
                                                    width: "85%",
                                                    maxWidth: 355
                                                }
                                            }
                                        }
                                    }}
                                    sx={{ width: belowsm ? "70%" : "90%" }}
                                    disabled={user.userID === "account-not-active"}
                                >
                                    {representations?.map((r, i) => (
                                        <MenuItem key={`item-${i}`} value={r}>
                                            <Typography>
                                                {`${r.familyName} ${r.surName}`}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </StyledTextField>
                            ) : (
                                <Autocomplete
                                    id="default-proxy-autocomplete"
                                    options={representations}
                                    getOptionLabel={(option) => `${option.familyName} ${option.surName}`}
                                    value={update.defaultProxy || null}
                                    onChange={(event, newValue) => {
                                        setUpdate({
                                                ...update,
                                                defaultProxy: newValue
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <StyledTextField
                                            {...params}
                                            variant="standard"
                                            label={t("default-proxy")}
                                            size="medium"
                                            helperText=" "
                                            InputLabelProps={{ shrink: true }}
                                            disabled={user.userID === "account-not-active"}
                                        />
                                    )}
                                    disableClearable
                                    isOptionEqualToValue={(option, value) => option.userID === value?.userID}
                                    sx={{ width: belowsm ? "70%" : "90%" }}
                                    disabled={user.userID === "account-not-active"}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                            <StyledTextField
                                variant="standard"
                                label={t("virtual-proxy")}
                                helperText=" "
                                value={update.defaultVirtualProxy}
                                onChange={(event) => {
                                    setUpdate({
                                        ...update,
                                        defaultVirtualProxy: event.target.value
                                    });
                                }}
                                select
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                    renderValue: (rep) => (<Typography>{`${rep.surName} ${rep.familyName}`}</Typography>),
                                    MenuProps: {
                                        sx: {
                                            "& .MuiMenu-paper": {
                                                bgcolor: "background.default",
                                                backgroundImage: "none",
                                                mt: .5,
                                                width: "85%",
                                                maxWidth: 355
                                            }
                                        }
                                    }
                                }}
                                sx={{ width: belowsm ? "70%" : "90%" }}
                                disabled={user.userID === "account-not-active"}
                            >
                                {virtualRepresentations?.map((r, i) => (
                                    <MenuItem key={`item-${i}`} value={r}>
                                        <Typography>
                                            {`${r.surName} ${r.familyName}`}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </StyledTextField>
                        </Grid>
                        
                    </Grid>
                </Grid>
            </DialogContent>
            <Grid container sx={{ p: 1, pl: 3, pr: belowsm ? 1 : 3, pb: belowsm ? 2 : 1 }}>
                <Typography color="text.secondary" fontSize={12} sx={{ alignSelf: "center" }}>Version {process.env.REACT_APP_VERSION || "X.X.X"}</Typography>
                <Box
                    key="spacer"
                    sx={{ flexGrow: 1 }}
                />
                <Button
                    variant="contained"
                    disabled={!user.mobilboxPassword || update.mobilboxPassword === ""}
                    onClick={() => { setOpen(false); document.title = titleRef.current; }}
                    sx={{
                        width: belowsm ? "40%" : "auto",
                        mr: 1,
                        backgroundColor: "primary.dark",
                        textTransform: "none",
                        border: theme.palette.mode === "dark" ? 1 : 0,
                        borderColor: theme.palette.mode === "dark" ? "primary.main" : "primary.dark",
                        borderRadius: 6,
                        "&:hover": { backgroundColor: "primary.dark" },
                        "&:disabled": { backgroundColor: "action.disableBackground", border: 1, borderColor: "action.hover" }
                    }}
                >
                    <Typography sx={{ color: !user.mobilboxPassword || update.mobilboxPassword === "" ? "text.primary" : "primary.contrastText", fontSize: 16 }}>{t("close")}</Typography>
                </Button>
                <LoadingButton
                    variant="contained"
                    disabled={_.isEqual(user, update)}
                    loading={loading}
                    onClick={save}
                    sx={{
                        width: belowsm ? "40%" : "auto",
                        backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
                        textTransform: "none",
                        border: 1,
                        borderColor: _.isEqual(user, update) || loading ? "action.hover" : "primary.main",
                        borderRadius: 6,
                        "&:hover": { backgroundColor: "primary.dark" },
                        "&:disabled": { backgroundColor: "action.disableBackground" }
                    }}
                >
                    <Typography sx={{ color: _.isEqual(user, update) ? "text.primary" : "primary.contrastText", fontSize: 16, visibility: loading ? "hidden" : "visible" }}>{t("save")}</Typography>
                </LoadingButton>
            </Grid>
        </Dialog >
    )
});

export default UserSettings;